import type { ChatSessionSummary } from '../../contexts/ChatContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useChat } from '../../contexts/ChatContext';
import ChatWindow from './ChatWindow';

/**
 * Global ChatWindow component that connects to ChatContext
 * Place this at the App level for application-wide chat functionality
 */
export function GlobalChatWindow() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    isOpen,
    messages,
    isLoading,
    title,
    threadId,
    closeChat,
    sendMessage,
    suggestedPrompts,
    selectSuggestedPrompt,
    contextInfo,
    sessions,
    sessionsLoading,
    loadSessions,
    switchSession,
    startNewSession,
    deleteSession,
  } = useChat();

  // Hide chat window during onboarding flow
  const isOnboardingRoute = location.pathname.startsWith('/onboarding');
  const shouldShowChat = isOpen && !isOnboardingRoute;

  // Session history shows across all chat types for company-scoped + generic.
  // Account plan chats render their own drawer (not this component).
  const showSessionControls = true;

  const handleSelectSession = (session: ChatSessionSummary) => {
    if (session.kind === 'plan' && session.targetId) {
      navigate(`/account-plan/${session.targetId}`);
      return;
    }
    if (session.kind === 'company' && session.companyName) {
      navigate(`/company/${encodeURIComponent(session.companyName)}`);
      return;
    }
    if (session.kind === 'generic') {
      switchSession(session.threadId);
    }
  };

  return (
    <ChatWindow
      open={shouldShowChat}
      onClose={closeChat}
      messages={messages}
      title={title}
      isLoading={isLoading}
      onSendMessage={sendMessage}
      showInput={true}
      suggestedPrompts={suggestedPrompts}
      onSuggestedPromptClick={selectSuggestedPrompt}
      contextInfo={contextInfo}
      sessions={sessions}
      sessionsLoading={sessionsLoading}
      activeThreadId={threadId}
      showSessionControls={showSessionControls}
      onLoadSessions={loadSessions}
      onSelectSession={handleSelectSession}
      onNewSession={startNewSession}
      onDeleteSession={deleteSession}
    />
  );
}

export default GlobalChatWindow;
